import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-solid-svg-icons';

// Animações
const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); opacity: 0.7; }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.3); }
  50% { box-shadow: 0 0 20px rgba(64, 128, 255, 0.6); }
  100% { box-shadow: 0 0 5px rgba(64, 128, 255, 0.3); }
`;

const glitch = keyframes`
  0% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
  2% {
    clip-path: inset(10% 0 20% 0);
    transform: translate(-2px);
  }
  4% {
    clip-path: inset(40% 0 5% 0);
    transform: translate(2px);
  }
  6% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
  100% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
`;

const scanLine = keyframes`
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100%);
  }
`;

const dataFlow = keyframes`
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 100% 50%;
  }
`;

// Estilos
const SelectorContainer = styled.div`
  width: 100%;
  margin-bottom: 3.5rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    background-image: 
      radial-gradient(circle at 10% 20%, rgba(100, 182, 255, 0.03) 1px, transparent 2px),
      radial-gradient(circle at 20% 40%, rgba(100, 182, 255, 0.03) 1px, transparent 2px),
      radial-gradient(circle at 30% 60%, rgba(100, 182, 255, 0.03) 1px, transparent 2px),
      radial-gradient(circle at 40% 80%, rgba(100, 182, 255, 0.03) 1px, transparent 2px);
    background-size: 20% 20%;
  }
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.8rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  
  svg {
    color: #64B6FF;
    filter: drop-shadow(0 0 2px rgba(100, 182, 255, 0.8));
  }
`;

const DurationContainer = styled.div`
  position: relative;
  width: 100%;
  padding: 5px 0;
  margin-top: 10px;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(
      to right,
      transparent,
      rgba(100, 182, 255, 0.2),
      rgba(100, 182, 255, 0.5),
      rgba(100, 182, 255, 0.2),
      transparent
    );
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: 
      radial-gradient(circle at 20% 50%, rgba(100, 182, 255, 0.1) 1px, transparent 1px),
      radial-gradient(circle at 40% 50%, rgba(100, 182, 255, 0.1) 1px, transparent 1px),
      radial-gradient(circle at 60% 50%, rgba(100, 182, 255, 0.1) 1px, transparent 1px),
      radial-gradient(circle at 80% 50%, rgba(100, 182, 255, 0.1) 1px, transparent 1px);
    background-size: 5% 100%;
    z-index: 0;
    pointer-events: none;
  }
`;

const DataFlowEffect = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  width: ${props => ((props.value - props.min) / (props.max - props.min)) * 100}%;
  height: 2px;
  background: linear-gradient(90deg, 
    rgba(76, 110, 245, 0.8), 
    rgba(100, 182, 255, 0.8), 
    rgba(76, 110, 245, 0.8)
  );
  background-size: 200% 100%;
  animation: ${dataFlow} 2s linear infinite;
  z-index: 1;
  filter: blur(1px);
`;

const RangeSlider = styled.input.attrs({ type: 'range' })`
  -webkit-appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: linear-gradient(to right, 
    #1E3A8A 0%, 
    #3B82F6 ${props => ((props.value - props.min) / (props.max - props.min)) * 100 * 0.9}%, 
    rgba(96, 165, 250, 0.05) ${props => ((props.value - props.min) / (props.max - props.min)) * 100 + 5}%,
    rgba(96, 165, 250, 0.02) 100%
  );
  outline: none;
  position: relative;
  margin: 20px 0;
  
  /* Marcações de escala */
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 20px;
    top: -10px;
    left: 0;
    background-image: repeating-linear-gradient(
      to right,
      transparent,
      transparent calc(100% / 21 - 1px),
      rgba(100, 182, 255, 0.3) calc(100% / 21)
    );
    pointer-events: none;
  }
  
  /* Trilha ativa */
  &::after {
    content: '';
    position: absolute;
    height: 6px;
    left: 0;
    top: 0;
    width: ${props => ((props.value - props.min) / (props.max - props.min)) * 100}%;
    background: linear-gradient(to right, #4C6EF5, #64B6FF);
    border-radius: 3px;
    box-shadow: 0 0 10px rgba(100, 182, 255, 0.5);
    pointer-events: none;
    z-index: 1;
  }
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #fff;
    cursor: pointer;
    border: 2px solid #3B82F6;
    box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
    transition: all 0.2s ease;
    position: relative;
    z-index: 2;
  }
  
  &::-webkit-slider-thumb:hover {
    transform: scale(1.1);
    box-shadow: 0 0 15px rgba(59, 130, 246, 0.7);
  }
`;

const RangeValue = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 0.5rem;
  position: relative;
  
  span {
    background: rgba(20, 30, 48, 0.8);
    color: #64B6FF;
    padding: 8px 15px;
    border-radius: 4px;
    font-weight: bold;
    font-family: 'Courier New', monospace;
    letter-spacing: 1px;
    border: 1px solid rgba(100, 182, 255, 0.5);
    box-shadow: 
      0 0 10px rgba(100, 182, 255, 0.5),
      inset 0 0 5px rgba(100, 182, 255, 0.3);
    text-shadow: 0 0 5px rgba(100, 182, 255, 0.8);
    animation: ${pulse} 2s infinite ease-in-out, ${glitch} 10s infinite;
    position: absolute;
    left: ${props => `calc(${((props.value - props.min) / (props.max - props.min)) * 100}% - 30px)`};
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        to bottom,
        transparent 0%,
        rgba(100, 182, 255, 0.1) 50%,
        transparent 100%
      );
      animation: ${scanLine} 2s linear infinite;
      z-index: 1;
      pointer-events: none;
    }
    
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      width: 2px;
      height: 6px;
      background: rgba(100, 182, 255, 0.8);
      box-shadow: 0 0 5px rgba(100, 182, 255, 0.8);
    }
  }
`;

// Efeito de partículas
const ParticleCanvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  opacity: 0.5;
`;

// Componente principal
const DurationSelector = ({ value = 60, onChange, min = 5, max = 120, step = 5, disabled = false }) => {
  const percentage = ((value - min) / (max - min)) * 100;
  const canvasRef = useRef(null);
  
  // Efeito para criar partículas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const particles = [];
    
    // Ajustar o tamanho do canvas
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Criar partículas
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.color = `rgba(100, 182, 255, ${Math.random() * 0.2})`;
      }
      
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        
        if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
        if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;
      }
      
      draw() {
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    
    // Inicializar partículas
    const init = () => {
      for (let i = 0; i < 15; i++) {
        particles.push(new Particle());
      }
    };
    
    init();
    
    // Animar partículas
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw();
      }
      
      requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);
  
  return (
    <SelectorContainer>
      <ParticleCanvas ref={canvasRef} />
      <Label>
        <FontAwesomeIcon icon={faClock} pulse /> Duração em Minutos
      </Label>
      <DurationContainer>
        <DataFlowEffect value={value} min={min} max={max} />
        <RangeSlider
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          disabled={disabled}
        />
      </DurationContainer>
      <RangeValue value={value} min={min} max={max}>
        <span>{value} min</span>
      </RangeValue>
    </SelectorContainer>
  );
};

export default DurationSelector;
